import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Input } from 'reactstrap';
import moment from 'moment';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import * as actions from './actions';
import { Button } from 'components/Button/RoundButton';
import { Form } from 'components/FormComponents/FormComponents';
import { Container, Wrapper } from 'components/Layout/Layout';
import { Header, HeadingContainer } from 'components/PageTitleHeader/styles';
import { InputAndSearchSection, InputSection, CreditsTag, PaymentNotPresentContainer } from './styles';
import {
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableHeading,
  TableData,
  SortArrowImage,
} from 'components/CMSRankList/styles';
import Pagination from 'components/Pagination/Pagination';
import { userRoles } from 'constants/users';
import { useActions } from 'hooks/useActions';
import { Images } from 'assets/images';
import * as AuthActions from 'global/actions/authActions';
import Path from 'routes/Path';

const itemsPerPage = 10;

export default React.memo(() => {
  const [transactions, setTransactions] = useState([]);
  const [paginatedItems, setPaginatedItems] = useState([]);
  const [sortBy, setSortBy] = useState('name');
  const [isAscending, setIsAscending] = useState(false);
  const [comment, setComment] = useState('');
  const [credits, setCredits] = useState(100);

  const navigate = useNavigate();

  const { loading, payments, transaction, error } = useSelector((state) => state.PaymentsPageReducer);
  const { userRole } = useSelector((state) => state.auth);

  const { getPayments, createPayment } = useActions(actions);
  const { verifyAuthState } = useActions(AuthActions);

  const isSuperAdminOrReseller = userRole === userRoles.SUPER_ADMIN || userRole === userRoles.RESELLER;

  useEffect(() => {
    getPayments();
  }, [getPayments]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  useEffect(() => {
    if (transaction.payment) {
      verifyAuthState();
      setComment('');
      setCredits(100);
      toast.success('Transaction successful');
    }
  }, [transaction, verifyAuthState]);

  useEffect(() => {
    setTransactions(payments);
    if (transactions.length <= itemsPerPage) setPaginatedItems(payments);
  }, [payments, transactions]);

  const sort = (array, field, isAscending) => {
    let sortableItems = [...array];
    return sortableItems.sort((a, b) => {
      let x = field === 'accountName' ? a[field].toLowerCase() : a[field];
      let y = field === 'accountName' ? b[field].toLowerCase() : b[field];
      if (x < y) {
        return isAscending ? -1 : 1;
      }
      if (x > y) {
        return isAscending ? 1 : -1;
      }
      return 0;
    });
  };

  const sortListBy = (field) => {
    let updatedList = sort(transactions, field, field !== sortBy ? false : isAscending);
    setTransactions(updatedList);
    if (field !== sortBy) {
      setIsAscending(true);
    } else {
      setIsAscending(!isAscending);
    }
    setSortBy(field);
  };

  const createManualPayment = () => {
    if (!Number(credits) || Number(credits) % 100 !== 0) return toast.error('Credits should be multiple of 100');
    let body = {
      credits: Number(credits),
      comment: comment,
      isManualPayment: true,
    };
    createPayment(body);
  };

  const renderTopSection = () => (
    <Form onSubmit={() => createManualPayment()}>
      <InputAndSearchSection>
        <InputSection>
          <Input
            type="text"
            placeholder="Comment (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </InputSection>
        <InputSection>
          <Input type="number" placeholder="Credits" value={credits} onChange={(e) => setCredits(e.target.value)} />
        </InputSection>
        <InputSection>
          <Button type="submit" padding="5px 15px" margin="0" primaryBlue primaryGreenBorder>
            Create Payment
          </Button>
        </InputSection>
      </InputAndSearchSection>
    </Form>
  );

  const renderTableHeading = () => (
    <TableRow isHeading>
      <TableHeading>
        Account
        <SortArrowImage
          onClick={() => sortListBy('accountName')}
          alt="arrow"
          src={sortBy === 'accountName' && isAscending ? Images.upArrow : Images.downArrow}
        />
      </TableHeading>
      <TableHeading hideOnSmallDevice>Comment</TableHeading>
      <TableHeading hideOnSmallDevice>Active Plan</TableHeading>
      <TableHeading>
        Date
        <SortArrowImage
          onClick={() => sortListBy('createdAt')}
          alt="arrow"
          src={sortBy === 'createdAt' && isAscending ? Images.upArrow : Images.downArrow}
        />
      </TableHeading>
      <TableHeading>
        Tests
        <SortArrowImage
          onClick={() => sortListBy('credits')}
          alt="arrow"
          src={sortBy === 'credits' && isAscending ? Images.upArrow : Images.downArrow}
        />
      </TableHeading>
      <TableHeading>Total Tests</TableHeading>
      <TableHeading>Receipt</TableHeading>
    </TableRow>
  );

  const renderTableBody = () =>
    paginatedItems.map((item, index) => {
      return (
        <TableRow key={index}>
          <TableData>{item.accountName}</TableData>
          <TableData hideOnSmallDevice>{item.comment}</TableData>
          <TableData hideOnSmallDevice>{item.plan || ''}</TableData>
          <TableData>{moment(item.createdAt).format('YYYY-MM-DD')}</TableData>
          <TableData>
            <CreditsTag isPositive={item.credits / 100 > 0}>
              {item.credits / 100} {Math.abs(item.credits) / 100 === 1 ? 'test' : 'tests'}
            </CreditsTag>
          </TableData>
          <TableData>
            {item.newTotal && Math.floor(item.newTotal / 100)}{' '}
            {item.newTotal >= 0 ? (Math.floor(item.newTotal / 100) > 1 ? 'tests' : 'test') : ''}
            {item.payStatus && item.payStatus !== 'succeeded' && item.payStatus && item.payStatus !== 'trial' && (
              <CreditsTag isPositive={false}>Failed</CreditsTag>
            )}
          </TableData>
          <TableData>
            {item.receipt_url && (
              <a href={item.receipt_url} target="_blank" rel="noopener noreferrer">
                <i className="fa fa-external-link"></i>
              </a>
            )}
          </TableData>
        </TableRow>
      );
    });

  return (
    <Wrapper padding="60px 0 0 0" minHeight="100vh">
      <Container column>
        <HeadingContainer>
          <Header>Payments</Header>
          {!isSuperAdminOrReseller && (
            <Button
              padding="5px 15px"
              margin="10px 0 0 0"
              onClick={() => navigate(Path.subscriptionPlans)}
              primaryBlue
              primaryGreenBorder
            >
              Purchase Tests
            </Button>
          )}
        </HeadingContainer>
        {isSuperAdminOrReseller && renderTopSection()}

        {loading && 'Loading'}
        {!loading && !transactions?.length && (
          <PaymentNotPresentContainer>Payment history not present</PaymentNotPresentContainer>
        )}
        {!loading && !!transactions?.length && (
          <>
            <Table margin={isSuperAdminOrReseller ? '' : '30px 0 0 0'}>
              <TableHead>{renderTableHeading()}</TableHead>
              <TableBody>{renderTableBody()}</TableBody>
            </Table>
            {transactions.length > itemsPerPage && (
              <Pagination items={transactions} itemsPerPage={itemsPerPage} getItems={setPaginatedItems} />
            )}
          </>
        )}
      </Container>
    </Wrapper>
  );
});
